import { ExcelActivitiesData } from "#models/activities/activity.model";
import { ExcelLookupData } from "#models/lookups/excel-lookup-data.model";
import { Injectable } from "@angular/core";
import * as Excel from "exceljs";

@Injectable({
  providedIn: "root",
})
export class ExcelService {
  private readonly templateValidationRowCount = 500;

  async importFromFile(bstr: string | ArrayBuffer): Promise<ExcelLookupData[]> {
    const data: ExcelLookupData[] = [];
    const wb = new Excel.Workbook();
    const workBook = await wb.xlsx.load(bstr as Buffer);
    workBook.eachSheet((sheet, id) => {
      const columnNames = new Map<number, string>();
      sheet.eachRow((row, rowIndex) => {
        if (rowIndex === 1) {
          const firstRow = (row.values as string[]).slice(1);
          for (const [index, column] of firstRow.entries()) {
            columnNames.set(index + 1, column);
          }
        }

        if (rowIndex > 1) {
          const newObj = new ExcelLookupData();
          row.eachCell((cell, colNum) => {
            if (rowIndex > 1) {
              newObj[columnNames.get(colNum)] = cell.value;
            }
          });
          data.push(newObj);
        }
      });
    });

    return data;
  }

  async importActivitiesFromFile(
    bstr: string | ArrayBuffer
  ): Promise<ExcelActivitiesData[]> {
    const data: ExcelActivitiesData[] = [];
    const wb = new Excel.Workbook();
    const workBook = await wb.xlsx.load(bstr as Buffer);
    workBook.eachSheet((sheet, id) => {
      
      if (sheet.state === "hidden" || sheet.state === "veryHidden") {
        return;
      }
      const columnNames = new Map<number, string>();
      sheet.eachRow((row, rowIndex) => {
        if (rowIndex === 1) {
          const firstRow = (row.values as string[]).slice(1);
          for (const [index, column] of firstRow.entries()) {
            columnNames.set(index + 1, column);
          }
        }

        if (rowIndex > 1) {
          const newObj = new ExcelActivitiesData();
          row.eachCell((cell, colNum) => {
            if (rowIndex > 1) {
              newObj[columnNames.get(colNum)] = cell.value;
            }
          });
          data.push(newObj);
        }
      });
    });

    return data;
  }

  async exportActivitiesTemplate(
    fileName?: string,
    cvxPhaseOptions: string[] = [],
    primaryResourceOptions: string[] = []
  ): Promise<void> {
    const wb = new Excel.Workbook();
    const ws = wb.addWorksheet("Activities Template");
    const lookupSheet = wb.addWorksheet("Template Lookup");
    lookupSheet.state = "veryHidden";

    // Define columns with headers - matching the required format
    ws.columns = [
      { header: "Name", key: "Name", width: 25 },
      { header: "CVXPhase", key: "CVXPhase", width: 20 },
      { header: "PrimaryResource", key: "PrimaryResource", width: 25 },
      { header: "CrewSize", key: "CrewSize", width: 15 },
      { header: "Duration", key: "Duration", width: 15 },
    ];

    // Style the header row
    ws.getRow(1).font = { bold: true, color: { argb: "FFFFFFFF" } };
    ws.getRow(1).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF4472C4" },
    };

    this.populateLookupColumn(lookupSheet, "A", cvxPhaseOptions);
    this.populateLookupColumn(lookupSheet, "B", primaryResourceOptions);

    this.applyDropdownValidation(
      ws,
      2,
      lookupSheet,
      "A",
      cvxPhaseOptions.length,
      "Select a valid CVX Phase from the list."
    );
    this.applyDropdownValidation(
      ws,
      3,
      lookupSheet,
      "B",
      primaryResourceOptions.length,
      "Select a valid Primary Resource from the list."
    );

    // Generate the file
    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName ? fileName + ".xlsx" : "Activities_Template.xlsx";
    link.click();
    window.URL.revokeObjectURL(url);
  }

  private applyDropdownValidation(
    worksheet: Excel.Worksheet,
    columnIndex: number,
    sourceSheet: Excel.Worksheet,
    sourceColumnLetter: string,
    optionCount: number,
    errorMessage: string
  ): void {
    if (!optionCount) {
      return;
    }

    const sourceRange = `'${sourceSheet.name}'!$${sourceColumnLetter}$1:$${sourceColumnLetter}$${optionCount}`;

    for (let rowIndex = 2; rowIndex <= this.templateValidationRowCount; rowIndex++) {
      worksheet.getCell(rowIndex, columnIndex).dataValidation = {
        type: "list",
        allowBlank: true,
        formulae: [sourceRange],
        showErrorMessage: true,
        errorStyle: "error",
        errorTitle: "Invalid selection",
        error: errorMessage,
      };
    }
  }

  private populateLookupColumn(
    worksheet: Excel.Worksheet,
    columnLetter: string,
    options: string[]
  ): void {
    const uniqueOptions = [...new Set(options.map((option) => option?.trim()).filter(Boolean))];

    uniqueOptions.forEach((option, index) => {
      worksheet.getCell(`${columnLetter}${index + 1}`).value = option;
    });
  }
}
